import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, ArrowRight, MapPin, Clock, CalendarDays, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

export default function CalendarBooking() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [province, setProvince] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());

  // Load quote and extract province
  useEffect(() => {
    api.get(`/quotes/${quoteId}`)
      .then(async (r) => {
        setQuote(r.data);
        let prov = r.data.province || '';
        // If no province saved, calculate from address
        if (!prov && r.data.address) {
          try {
            const distRes = await api.post('/distance/calculate', { address: r.data.address });
            prov = distRes.data.province || '';
          } catch { /* ignore */ }
        }
        setProvince(prov);
      })
      .catch(() => toast.error('Errore nel caricamento'))
      .finally(() => setLoading(false));
  }, [quoteId]);

  // Load shared calendar slots when province is known
  useEffect(() => {
    if (loading) return;
    setLoadingSlots(true);
    setSlots([]);
    setSelectedDate(null);
    setSelectedSlot('');
    const params = new URLSearchParams();
    if (province) params.append('province', province);
    api.get(`/appointments/slots?${params}`)
      .then((r) => setSlots(r.data))
      .catch(() => toast.error('Errore caricamento slot'))
      .finally(() => setLoadingSlots(false));
  }, [province, loading]);

  const availableDates = slots.map((s) => parseISO(s.date));

  const getAvailableSlots = (date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    const found = slots.find((s) => s.date === dateStr);
    return found ? found.available_slots : [];
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Seleziona data e orario');
      return;
    }
    setBooking(true);
    try {
      await api.post('/appointments', {
        quote_id: quoteId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot,
        zone: '',
        province: province,
        notes: '',
      });
      toast.success('Appuntamento prenotato!');
      navigate(`/pagamento/${quoteId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setBooking(false);
    }
  };

  const timeSlots = getAvailableSlots(selectedDate);
  const weekStart = startOfWeek(viewDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(viewDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd }).filter(d => d.getDay() !== 0 && d.getDay() !== 6);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="calendar-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/chat/${quoteId}`)} data-testid="calendar-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900">Prenota Appuntamento</p>
            {province && (
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Provincia di {province}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Province info */}
        {province && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 mb-6">
            <strong>Come funziona:</strong> Il calendario mostra solo i giorni in cui il tecnico opera nella tua provincia ({province}).
            Disponibilita' solo Lunedi, Giovedi e Venerdi.
          </div>
        )}

        {loadingSlots ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : slots.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-8 text-center">
              <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Nessuna data disponibile per la tua provincia nei prossimi 60 giorni.</p>
              <p className="text-xs text-slate-400 mt-1">Contattaci per trovare una soluzione.</p>
            </CardContent>
          </Card>
        ) : (
        <>
        <Tabs defaultValue="month" className="w-full">
          <TabsList className="mb-6 bg-white rounded-xl shadow-sm p-1">
            <TabsTrigger value="month" className="rounded-lg text-xs" data-testid="view-month">Mese</TabsTrigger>
            <TabsTrigger value="week" className="rounded-lg text-xs" data-testid="view-week">Settimana</TabsTrigger>
          </TabsList>

          <TabsContent value="month">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedSlot(''); }}
                    locale={it}
                    disabled={(date) => {
                      if (date < new Date()) return true;
                      return !availableDates.some((ad) => isSameDay(ad, date));
                    }}
                    modifiers={{ available: availableDates }}
                    modifiersStyles={{ available: { fontWeight: 'bold', color: '#3B82F6' } }}
                    className="w-full"
                    data-testid="calendar-monthly"
                  />
                </CardContent>
              </Card>

              <div>
                {selectedDate ? (
                  <Card className="border-0 shadow-sm rounded-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-blue-600" />
                        {format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {timeSlots.length > 0 ? timeSlots.map((slot) => (
                        <div
                          key={slot}
                          className={`slot-available ${selectedSlot === slot ? 'selected' : ''}`}
                          onClick={() => setSelectedSlot(slot)}
                          data-testid={`slot-${slot}`}
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span className="font-medium text-sm">{slot}</span>
                          </div>
                        </div>
                      )) : (
                        <p className="text-sm text-slate-500 text-center py-4">Nessuno slot disponibile</p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-0 shadow-sm rounded-xl">
                    <CardContent className="p-8 text-center">
                      <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Seleziona una data dal calendario</p>
                      <p className="text-xs text-slate-400 mt-1">Le date in blu hanno slot disponibili</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="week">
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setViewDate(addDays(viewDate, -7))}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <p className="font-semibold text-sm text-slate-900">
                {format(weekStart, 'd MMM', { locale: it })} - {format(weekEnd, 'd MMM yyyy', { locale: it })}
              </p>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setViewDate(addDays(viewDate, 7))}>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-5 gap-3">
              {weekDays.map((day) => {
                const daySlots = getAvailableSlots(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                return (
                  <Card key={day.toISOString()} className={`border shadow-sm rounded-xl ${isSelected ? 'border-blue-600' : 'border-slate-100'}`}>
                    <CardContent className="p-3">
                      <p className={`text-center text-xs font-semibold mb-2 ${isSelected ? 'text-blue-600' : 'text-slate-500'}`}>
                        {format(day, 'EEE d', { locale: it })}
                      </p>
                      {daySlots.length > 0 ? (
                        <div className="space-y-1.5">
                          {daySlots.map((slot) => (
                            <button
                              key={slot}
                              className={`w-full text-xs py-1.5 px-2 rounded-lg border transition-all ${
                                selectedDate && isSameDay(day, selectedDate) && selectedSlot === slot
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-slate-200 hover:border-blue-300 text-slate-600'
                              }`}
                              onClick={() => { setSelectedDate(day); setSelectedSlot(slot); }}
                              data-testid={`week-slot-${format(day, 'yyyy-MM-dd')}-${slot}`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-300 text-center py-2">--</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {selectedDate && selectedSlot && (
          <div className="mt-8">
            <Card className="border-0 shadow-sm rounded-xl bg-slate-900 text-white">
              <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm text-slate-400">Appuntamento selezionato</p>
                  <p className="font-bold">{format(selectedDate, 'EEEE d MMMM', { locale: it })} - {selectedSlot}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {province && `Provincia di ${province} | `}
                    EUR {quote?.price_min} - {quote?.price_max}
                  </p>
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                  onClick={handleBook}
                  disabled={booking}
                  data-testid="confirm-booking-btn"
                >
                  {booking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {booking ? 'Prenotazione...' : 'Conferma'}
                  <Check className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
